import { useEffect, useState } from "react";
import { Input, Form, Modal, Typography, Space, Badge, Checkbox } from "antd";
import { ApartmentOutlined } from "@ant-design/icons";

import CustomSelect from "@/components/inputs/CustomSelect";

import { createItem, updateItem, getUnitsOptions } from "@/api/units";
import { getEmployeesOptions } from "@/api/employees";
import { getUnitTypeOptions } from "@/api/unitTypes";

const { Text } = Typography;

// Окно для создания/редактирования РГ, содержит форму
const UnitModal = ({ data, visible, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [unitTypesList, setUnitTypesList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [userList, setUserList] = useState([]);

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  const fetchOptions = async () => {
    try {
      const [{ types }, { units }, { employees }] = await Promise.all([
        getUnitTypeOptions({ deleted: false }),
        getUnitsOptions(),
        getEmployeesOptions({ is_dismissed: false }),
      ]);

      setUnitTypesList(
        types
          .filter((type) => !type.is_personal)
          .map((item) => {
            const { name, is_hierarchical } = item;
            return {
              ...item,
              label: (
                <Space>
                  <span>{name}</span>
                  <Badge
                    count={
                      is_hierarchical ? (
                        <ApartmentOutlined
                          style={{
                            color: "var(--color-primary)",
                          }}
                        />
                      ) : (
                        0
                      )
                    }
                  />
                </Space>
              ),
            };
          })
      );

      setUnitList(
        units
          .filter(({ name }) => name !== data?.name)
          .map((item) => {
            return {
              ...item,
              label: item.name,
            };
          })
      );

      if (
        data?.manager &&
        !employees.find(({ employee }) => employee.uuid === data.manager.uuid)
      ) {
        employees.push({ employee: data.manager });
      }

      setUserList(
        employees.map((item) => {
          const { lastname, firstname, patronymic } = item.employee;
          return {
            ...item.employee,
            label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
          };
        })
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    try {
      if (data) {
        await updateItem({ ...data, ...values });
      } else {
        await createItem(values);
      }

      onSave();
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () =>
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });

  return (
    <Modal
      maskClosable={false}
      title={
        data
          ? "Редактирование рабочей группы"
          : "Добавление новой рабочей группы"
      }
      open={visible}
      onOk={onOk}
      onCancel={handleCancel}
      okText={data ? "Сохранить" : "Добавить"}
      cancelText="Отмена"
      okButtonProps={{ loading: isLoading }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={data}
        onFinish={handleSave}
      >
        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: "Пожалуйста, введите название" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="unit_type_uuid"
          label="Тип"
          rules={[{ required: true, message: "Пожалуйста, выберите тип " }]}
        >
          <CustomSelect
            placeholder="Начните вводить"
            options={unitTypesList}
            showSearch
            allowClear
            fieldNames={{ value: "uuid" }}
            optionFilterProp="name"
            disabled={data?.uuid}
          />
        </Form.Item>

        <Form.Item name="parent_uuid" label="Родительская рабочая группа">
          <CustomSelect
            placeholder="Начните вводить"
            options={unitList}
            showSearch
            allowClear
            fieldNames={{ value: "uuid" }}
            optionFilterProp="label"
            onChange={(value) => {
              if (value === undefined) {
                form.setFieldsValue({ parent_uuid: null });
              }
            }}
          />
        </Form.Item>

        <Form.Item name="manager_uuid" label="Руководитель">
          <CustomSelect
            placeholder="Начните вводить"
            options={userList}
            showSearch
            allowClear
            fieldNames={{ value: "uuid" }}
            optionFilterProp="label"
            onChange={(value) => {
              if (value === undefined) {
                form.setFieldsValue({ manager_uuid: null });
              }
            }}
          />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input.TextArea rows={5} />
        </Form.Item>

        <Form.Item name="disallow_parent_access" valuePropName="checked">
          <Checkbox>Приватная рабочая группа</Checkbox>
        </Form.Item>

        {error && <Text type="danger">{error}</Text>}
      </Form>
    </Modal>
  );
};

export default UnitModal;
